import { useCallback, useEffect, useRef, useState } from 'react'
import type { ActivePicker } from './types'
import { useHotkeys } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { isEmpty, isNil, last } from 'lodash-es'
import type { RouteSegment, StreetNodePlanningData, StreetSegmentPlanningData } from '@/api/main/types.gen'
import { resolveEscapeAction } from './escapeLadder'
import type { GapFix } from './gapFix'
import { decideAddModeNodeClick } from './mapHitTest'
import { editableVisitNumbers, shouldShowVisitPicker } from './nodeLabels'
import { removalLoopBounds, type RemovalPlan } from './removalRange'
import { shouldToggleCloseVisitPicker } from './visitPickerDismiss'
import type { EditState, PathState, PendingRemoval } from './types'

function isInputFocused() {
  const el = document.activeElement
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement
}

type PlannerInteractionsDeps = {
  routeExists: boolean
  selectedRouteId: string | undefined
  selectedRouteVersion: string | undefined
  pathState: PathState
  pathNodes: string[]
  editState: EditState | null
  canUndoSegment: boolean
  streetNodes: Record<string, StreetNodePlanningData>
  continuationSegments: StreetSegmentPlanningData[]
  indexedSegments: Record<string, StreetSegmentPlanningData>
  nonBlockedStreetSegments: StreetSegmentPlanningData[]
  appendToPath: (exitNode: Pick<StreetNodePlanningData, 'street_node_id'>, segment?: StreetSegmentPlanningData) => void
  deleteLastSegment: () => void
  startEdit: (segmentIndex: number) => void
  cancelEdit: () => void
  commitEdit: () => void
  canCommitEdit: boolean
  acceptTail: () => void
  canAcceptTail: boolean
  acceptShortestPath: (hint: RouteSegment[]) => void
  removeLoop: (loopStart: number, loopEnd: number, replacement?: RouteSegment[]) => void
  applyGapFixes: (fixes: GapFix[]) => void
  isDeleteMode: boolean
  isPathBuildMode: boolean
  addTarget: string | null
  setAddTarget: (id: string | null) => void
  pendingRemoval: PendingRemoval | null
  setPendingRemoval: (removal: PendingRemoval | null) => void
  activePicker: ActivePicker | null
  setActivePicker: (picker: ActivePicker | null) => void
  removalPlan: RemovalPlan | null
  shortestPathHint: RouteSegment[] | null
  hintReconnectNodeId: string | null
  gapFixes: GapFix[]
  applyRemovalAtIndex: (rawIdx: number) => void
  t: (key: string, options?: Record<string, unknown>) => string
}

export function usePlannerInteractions(deps: PlannerInteractionsDeps) {
  const {
    routeExists,
    selectedRouteId,
    selectedRouteVersion,
    pathState,
    pathNodes,
    editState,
    canUndoSegment,
    streetNodes,
    continuationSegments,
    indexedSegments,
    nonBlockedStreetSegments,
    appendToPath,
    deleteLastSegment,
    startEdit,
    cancelEdit,
    commitEdit,
    canCommitEdit,
    acceptTail,
    canAcceptTail,
    acceptShortestPath,
    removeLoop,
    applyGapFixes: applyGapFixesAction,
    isDeleteMode,
    isPathBuildMode,
    addTarget,
    setAddTarget,
    pendingRemoval,
    setPendingRemoval,
    activePicker,
    setActivePicker,
    removalPlan,
    shortestPathHint,
    hintReconnectNodeId,
    gapFixes,
    applyRemovalAtIndex,
    t,
  } = deps

  const [editAckOpen, setEditAckOpen] = useState(false)
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false)
  const [pendingSave, setPendingSave] = useState<(() => void) | null>(null)
  const editAcknowledgedRef = useRef(false)
  const pendingEditActionRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    editAcknowledgedRef.current = false
  }, [selectedRouteId, selectedRouteVersion])

  const guardEdit = useCallback(
    (action: () => void) => {
      if (routeExists && !editAcknowledgedRef.current) {
        pendingEditActionRef.current = action
        setEditAckOpen(true)
        return
      }
      action()
    },
    [routeExists]
  )

  const deleteLastSegmentGuarded = useCallback(() => {
    if (!canUndoSegment) {
      return
    }
    guardEdit(() => deleteLastSegment())
  }, [guardEdit, canUndoSegment, deleteLastSegment])

  const acceptShortestPathWithHistory = useCallback(
    (hint: RouteSegment[]) => {
      if (isEmpty(hint)) {
        notifications.show({ message: t('reconnectRequired'), color: 'orange' })
        return
      }
      guardEdit(() => {
        acceptShortestPath(hint)
        setAddTarget(null)
      })
    },
    [guardEdit, acceptShortestPath, setAddTarget, t]
  )

  const startEditWithHistory = useCallback(
    (segmentIndex: number) => {
      guardEdit(() => startEdit(segmentIndex))
    },
    [guardEdit, startEdit]
  )

  const commitEditWithHistory = useCallback(() => {
    if (!canCommitEdit) {
      notifications.show({ message: t('reconnectRequired'), color: 'orange' })
      return
    }
    commitEdit()
    setAddTarget(null)
  }, [canCommitEdit, commitEdit, t, setAddTarget])

  const cancelEditWithCleanup = useCallback(() => {
    cancelEdit()
    setAddTarget(null)
    setActivePicker(null)
  }, [cancelEdit, setAddTarget, setActivePicker])

  const confirmRemoval = useCallback(() => {
    const pr = pendingRemoval
    if (!pr || removalPlan?.status !== 'ready') return
    const { loopStart, loopEnd } = removalLoopBounds(pr)
    guardEdit(() => {
      removeLoop(loopStart, loopEnd, removalPlan.replacement)
      setPendingRemoval(null)
    })
  }, [pendingRemoval, removalPlan, removeLoop, guardEdit, setPendingRemoval])

  const applyGapFixes = useCallback(() => {
    if (isEmpty(gapFixes)) return
    guardEdit(() => applyGapFixesAction(gapFixes))
  }, [gapFixes, guardEdit, applyGapFixesAction])

  const handleVisitPickerSelect = useCallback(
    (visitNumber: number) => {
      if (!activePicker) return
      if (activePicker.action === 'edit') {
        startEditWithHistory(visitNumber - 1)
        setAddTarget(null)
      } else {
        applyRemovalAtIndex(visitNumber - 1)
      }
      setActivePicker(null)
    },
    [activePicker, startEditWithHistory, applyRemovalAtIndex, setAddTarget, setActivePicker]
  )

  const handleNodeClick = useCallback(
    (nodeId: string, clientPoint: { x: number; y: number }, opts?: { altKey?: boolean }) => {
      const node = streetNodes[nodeId]
      if (!node) return

      const editablePositions = editableVisitNumbers(pathNodes, nodeId)

      const matchingSegment = continuationSegments.find(
        it => it.destination_node_id === nodeId || (it.source_node_id === nodeId && it.is_bidirectional)
      )

      const isLastNode = nodeId === pathState.lastNode
      const lastEditablePos = editablePositions.at(-1)
      const editSegmentIndex = !editState && lastEditablePos !== undefined ? lastEditablePos - 1 : null
      const reconnectTargetNodeId = editState?.originalTail[0]?.exit_node_id
      const tailEndNodeId = last(editState?.originalTail ?? [])?.exit_node_id
      const isHintEnd = nodeId === hintReconnectNodeId || (!isNil(addTarget) && nodeId === addTarget)
      const isReconnectTarget = nodeId === reconnectTargetNodeId && !isPathBuildMode
      const isTailEndNode = editState !== null && nodeId === tailEndNodeId

      if (isDeleteMode) {
        if (isLastNode) {
          setActivePicker(null)
          applyRemovalAtIndex(pathNodes.length - 1)
          return
        }
        if (shouldShowVisitPicker('delete', editablePositions, isLastNode)) {
          if (shouldToggleCloseVisitPicker(activePicker, nodeId, 'delete')) {
            setActivePicker(null)
          } else {
            setActivePicker({
              nodeId,
              action: 'delete',
              visits: editablePositions,
              x: clientPoint.x,
              y: clientPoint.y,
            })
          }
          return
        }
        setActivePicker(null)
        if (lastEditablePos !== undefined) {
          applyRemovalAtIndex(lastEditablePos - 1)
        }
        return
      }

      const shouldAcceptHintOnThisNode = !isEmpty(shortestPathHint) && (isHintEnd || isReconnectTarget || isTailEndNode)

      // Alt-click on an editable path node skips continuation/hint handling and goes
      // straight to the mid-route edit branch (otherwise nodes adjacent to the path
      // end can never be edited — driving onto them always wins).
      const forceEditPick = Boolean(opts?.altKey) && !editState && !isNil(editSegmentIndex) && !isLastNode

      if (!forceEditPick) {
        const decision = decideAddModeNodeClick({
          hasContinuationMatch: matchingSegment !== undefined,
          shouldAcceptHintOnThisNode,
          canSetAddTarget: false,
        })

        if (decision === 'appendContinuation' && matchingSegment !== undefined) {
          setActivePicker(null)
          guardEdit(() => {
            appendToPath(node, matchingSegment)
            setAddTarget(null)
          })
          return
        }

        if (decision === 'acceptHint' && shortestPathHint) {
          setActivePicker(null)
          acceptShortestPathWithHistory(shortestPathHint)
          return
        }
      }

      if (!editState && !isNil(editSegmentIndex) && !isLastNode) {
        if (shouldShowVisitPicker('edit', editablePositions, isLastNode)) {
          if (shouldToggleCloseVisitPicker(activePicker, nodeId, 'edit')) {
            setActivePicker(null)
          } else {
            setActivePicker({
              nodeId,
              action: 'edit',
              visits: editablePositions,
              x: clientPoint.x,
              y: clientPoint.y,
            })
          }
        } else {
          setActivePicker(null)
          startEditWithHistory(editSegmentIndex)
          setAddTarget(null)
        }
        return
      }

      setActivePicker(null)

      if (isTailEndNode) {
        guardEdit(() => {
          if (!canAcceptTail) {
            notifications.show({ message: t('reconnectRequired'), color: 'orange' })
            return
          }
          acceptTail()
        })
        return
      }

      if (editState && !isLastNode && !isReconnectTarget && !isTailEndNode) {
        setAddTarget(nodeId)
        return
      }

      if (!editState && pathState.lastNode && pathState.lastNode !== nodeId) {
        setAddTarget(nodeId)
        return
      }

      guardEdit(() => appendToPath(node, matchingSegment))
    },
    [
      streetNodes,
      pathNodes,
      continuationSegments,
      pathState.lastNode,
      editState,
      isDeleteMode,
      isPathBuildMode,
      addTarget,
      activePicker,
      hintReconnectNodeId,
      shortestPathHint,
      guardEdit,
      appendToPath,
      acceptTail,
      canAcceptTail,
      startEditWithHistory,
      acceptShortestPathWithHistory,
      applyRemovalAtIndex,
      setAddTarget,
      setActivePicker,
      t,
    ]
  )

  const handleMapBackgroundClick = useCallback(() => {
    setActivePicker(null)
  }, [setActivePicker])

  const handleContinuationClick = useCallback(
    (segmentId: string) => {
      setActivePicker(null)
      const segment =
        indexedSegments[segmentId] ?? nonBlockedStreetSegments.find(s => s.street_segment_id === segmentId)
      if (!segment || !pathState.lastNode) return
      const reverseDrive = segment.source_node_id !== pathState.lastNode
      const exitNodeId = !reverseDrive ? segment.destination_node_id : segment.source_node_id
      const node = streetNodes[exitNodeId]
      if (!node) return
      guardEdit(() => appendToPath(node, segment))
    },
    [
      indexedSegments,
      nonBlockedStreetSegments,
      pathState.lastNode,
      streetNodes,
      guardEdit,
      appendToPath,
      setActivePicker,
    ]
  )

  useHotkeys([
    [
      'Backspace',
      () => {
        if (!isInputFocused()) {
          deleteLastSegmentGuarded()
        }
      },
    ],
    [
      'Enter',
      () => {
        if (isInputFocused()) return
        if (isDeleteMode) {
          confirmRemoval()
        } else if (shortestPathHint) {
          acceptShortestPathWithHistory(shortestPathHint)
        }
      },
    ],
    [
      'Escape',
      () => {
        const action = resolveEscapeAction({
          hasActivePicker: activePicker !== null,
          hasPendingRemoval: pendingRemoval !== null,
          hasAddTarget: addTarget !== null,
          hasEditState: editState !== null,
        })
        if (action === 'closePicker') setActivePicker(null)
        else if (action === 'clearRemoval') setPendingRemoval(null)
        else if (action === 'clearTarget') setAddTarget(null)
        else if (action === 'cancelEdit') cancelEditWithCleanup()
      },
    ],
  ])

  const confirmEditAck = useCallback(() => {
    editAcknowledgedRef.current = true
    setEditAckOpen(false)
    const action = pendingEditActionRef.current
    pendingEditActionRef.current = null
    action?.()
  }, [])

  const cancelEditAck = useCallback(() => {
    setEditAckOpen(false)
    pendingEditActionRef.current = null
  }, [])

  const requestSaveConfirm = useCallback((save: () => void) => {
    setPendingSave(() => save)
    setSaveConfirmOpen(true)
  }, [])

  const confirmSave = useCallback(() => {
    setSaveConfirmOpen(false)
    pendingSave?.()
    setPendingSave(null)
  }, [pendingSave])

  const cancelSave = useCallback(() => {
    setSaveConfirmOpen(false)
    setPendingSave(null)
  }, [])

  const clearActivePicker = useCallback(() => {
    setActivePicker(null)
  }, [setActivePicker])

  return {
    activePicker,
    setActivePicker,
    clearActivePicker,
    editAckOpen,
    confirmEditAck,
    cancelEditAck,
    saveConfirmOpen,
    requestSaveConfirm,
    confirmSave,
    cancelSave,
    guardEdit,
    deleteLastSegmentGuarded,
    acceptShortestPathWithHistory,
    startEditWithHistory,
    commitEditWithHistory,
    cancelEditWithCleanup,
    confirmRemoval,
    applyGapFixes,
    handleVisitPickerSelect,
    handleNodeClick,
    handleMapBackgroundClick,
    handleContinuationClick,
  }
}
